<script lang="ts">
	import type { DistanceUnit$options } from '$houdini';
	import type { Favorite } from './types';
	import Badge from '$lib/components/atoms/badge/Badge.svelte';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import SkeletonBlock from '$lib/components/atoms/skeleton/SkeletonBlock.svelte';
	import SkeletonButton from '$lib/components/atoms/skeleton/SkeletonButton.svelte';
	import SkeletonText from '$lib/components/atoms/skeleton/SkeletonText.svelte';
	import { distanceUnitLabel } from '$lib/components/constants';
	import Icon from '$lib/components/icons/Icon.svelte';
	import Table from '$lib/components/table/Table.svelte';
	import TableBodyCell from '$lib/components/table/TableBodyCell.svelte';
	import TableBodyRow from '$lib/components/table/TableBodyRow.svelte';
	import TableHead from '$lib/components/table/TableHead.svelte';
	import { gotoRelativeUrl } from '$lib/services/urls/searchParams';
	import { formatPriceInTwoParts } from '$lib/utils/formatPrice';
	import { getDurationHoursAndMinutes } from '$lib/utils/getDurationHoursAndMinutes';
	import * as m from '$paraglide/messages';
	import { formatBadges } from './badgeFormatter';

	interface Props {
		favorites: Favorite[];
		isLoading?: boolean;
		onDeleteFavorite?: (timestamp: Favorite['timestamp']) => void;
		onSelectFavorite?: (timestamp: Favorite['timestamp']) => void;
		onComputeItinerary?: (favorite: Favorite) => void;
		onCancelDeletion?: () => void;
	}

	const {
		favorites,
		isLoading = false,
		onDeleteFavorite,
		onSelectFavorite,
		onComputeItinerary,
		onCancelDeletion,
	}: Props = $props();

	let selectedFavorite = $state<Favorite['timestamp'] | null>(null);

	function handleSelectFavorite(timestamp: Favorite['timestamp']) {
		selectedFavorite = timestamp;
		onSelectFavorite?.(timestamp);
	}

	function handleComputeItinerary(favorite: Favorite) {
		onComputeItinerary?.(favorite);
	}

	function handleCancelDeletion() {
		selectedFavorite = null;
		onCancelDeletion?.();
	}

	function handleDeleteFavorite(timestamp: Favorite['timestamp']) {
		selectedFavorite = null;
		onDeleteFavorite?.(timestamp);
	}

	const costDurationTextClasses = 'flex items-center justify-start font-400 font-size-4 line-height-5 word-spacing-0.5 text-coloredneutral';
	const departureArrivalClasses = 'max-md:max-w-[calc(50%-10px)] overflow-hidden text-ellipsis whitespace-nowrap';
	const costDurationDistanceCellsClasses = 'max-md:w-80px max-md:grid-area-[numbers] max-md:flex max-md:items-center md:b-b md:border-neutral-soft py-2 md:py-4';
	const columnsClasses = 'pb-2.5 text-start';

	const columns = [
		{ label: m.bookmark__table_head_iti_details(), isSrOnly: true, class: columnsClasses },
		{ label: m.route__route_selector__column_duration(), class: columnsClasses },
		{ label: m.route_details__costs(), class: columnsClasses },
		{ label: m.route__route_selector__column_distance(), class: columnsClasses },
		{ label: m.bookmark__table_head_actions(), isSrOnly: true, class: columnsClasses },
	];
</script>

{#if !favorites.length && !isLoading}
	{@render EmptyFavoriteState()}
{:else}
	<Table>
		{#snippet header()}
			<TableHead columns={columns} />
		{/snippet}

		{#snippet body()}
			{#if isLoading}
				{#each [1, 2, 3] as n (n)}
					{@render SkeletonTable()}
				{/each}
			{:else}
				{#each favorites as favorite (favorite.timestamp)}
					{@const { departureName, arrivalName, steps, isEv, withCaravan, mode, totalCost, distance, duration, timestamp, constraints } = favorite}
					{@const { hours, minutes } = getDurationHoursAndMinutes(duration)}
					{@const { currency, amount, currencyAbbreviation } = formatPriceInTwoParts({ amount: totalCost.amount, currency: totalCost.currency, language: 'fr', digits: 2 })}
					{@const isFavoriteSelected = timestamp === selectedFavorite}

					<TableBodyRow rowClass={['[&:not(:first-child)]:max-md:pt-2.5', selectedFavorite && !isFavoriteSelected && 'opacity-60']}>
						<TableBodyCell cellClass="max-md:grid-area-[details] max-md:flex max-md:flex-wrap max-md:gap-1 max-md:items-start w-full md:w-auto font-400 md:py-4">
							<div class="max-md:w-full flex flex-row items-center justify-left font-size-4 line-height-5 text-coloredneutral font-600 py-0.5">
								<span class={departureArrivalClasses}>{departureName}</span>
								<Icon icon="chevron-right" class="text-5 min-w-5 self-end" />
								<span class={departureArrivalClasses}>{arrivalName}</span>
							</div>
							{#if steps.length > 0}
								<span class="max-md:w-full flex items-center gap-1 text-coloredneutral font-size-3.5 line-height-4 md:py-1">
									{m.bookmark__via()}
									<span class="text-coloredneutral-softest word-spacing-0.5 clamp-lines-1 whitespace-break-spaces">
										{steps.join(' • ')}
									</span>
								</span>
							{/if}
							<span class="flex flex-wrap gap-1 w-full overflow-hidden md:flex-nowrap">
								{#each formatBadges(isEv, withCaravan, mode, constraints) as badge (`${JSON.stringify(badge)}-${timestamp}`)}
									<Badge color="neutral" text={badge} size="sm" isSquare />
								{/each}
							</span>
						</TableBodyCell>
						<TableBodyCell cellClass={`${costDurationDistanceCellsClasses}`}>
							<time class={costDurationTextClasses} datetime={`${hours}:${minutes}`}>
								{hours} <span class="text-coloredneutral-soft px-0.5">h</span>
								{#if minutes && minutes > 0}
									{minutes} <span class="text-coloredneutral-soft px-0.5">min</span>
								{/if}
							</time>
						</TableBodyCell>
						<TableBodyCell cellClass={`max-md:pl-112px ${costDurationDistanceCellsClasses}`}>
							{@render ValueWithUnitCellContent(amount, currency, currencyAbbreviation)}
						</TableBodyCell>
						<TableBodyCell cellClass={`max-md:pl-214px ${costDurationDistanceCellsClasses}`}>
							<span class={costDurationTextClasses}>
								{@render ValueWithUnitCellContent(distance.value, distanceUnitLabel[distance.unit as unknown as DistanceUnit$options])}
							</span>
						</TableBodyCell>
						<TableBodyCell cellClass="max-md:grid-area-[actions] max-md:flex max-md:flex-row b-b border-neutral-soft text-left md:text-right py-2.5 md:py-4">
							<div class="flex flex-row-reverse gap-1 justify-end md:flex-row">
								{#if isFavoriteSelected}
									<Button
										size="sm"
										color="light"
										variant="outline"
										text={m.poi_actions__cancel()}
										onclick={handleCancelDeletion}
									/>
									<Button
										size="sm"
										color="danger"
										variant="filled"
										text={m.action__delete()}
										onclick={() => handleDeleteFavorite(timestamp)}
									/>
								{:else}
									<Button
										size="sm"
										color="light"
										variant="outline"
										icon="delete"
										ariaLabel={m.bookmark__delete_favorite_ariaLabel()}
										onclick={() => handleSelectFavorite(timestamp)}
									/>
									<Button
										size="sm"
										color="light"
										variant="filled"
										text={m.bookmark__see_favorite_route()}
										onclick={() => handleComputeItinerary(favorite)}
									/>
								{/if}
							</div>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			{/if}
		{/snippet}
	</Table>
{/if}

{#snippet SkeletonTable()}
	<TableBodyRow>
		<TableBodyCell cellClass="py-2 md:py-4 max-md:grid-area-[details]">
			<SkeletonText minWords={1} maxWords={3} minWordLength={4} maxWordLength={15} />
			<SkeletonText minWords={3} maxWords={4} minWordLength={4} maxWordLength={10} />
			<div class="py-0.5 flex gap-1 justify-start">
				<SkeletonBlock height="24px" width="87px" borderRadius="2px" />
				<SkeletonBlock height="24px" width="62px" borderRadius="2px" />
			</div>
		</TableBodyCell>
		<TableBodyCell cellClass={['md:text-center md:justify-start', costDurationDistanceCellsClasses]}>
			<SkeletonText minWords={1} maxWords={1} minWordLength={16} maxWordLength={16} />
		</TableBodyCell>
		<TableBodyCell cellClass={['md:text-center md:justify-start', costDurationDistanceCellsClasses]}>
			<SkeletonText minWords={1} maxWords={1} minWordLength={10} maxWordLength={10} class="max-md:pl-124px" />
		</TableBodyCell>
		<TableBodyCell cellClass={['md:text-center md:justify-start', costDurationDistanceCellsClasses]}>
			<SkeletonText minWords={1} maxWords={1} minWordLength={11} maxWordLength={11} class="max-md:pl-214px" />
		</TableBodyCell>
		<TableBodyCell cellClass="max-md:grid-area-[actions]">
			<div class="justify-start md:justify-end py-2 md:py-4 flex gap-1 md:flex-row">
				<SkeletonButton text={'1'} />
				<SkeletonButton text={m.bookmark__see_favorite_route()} />
			</div>
		</TableBodyCell>
	</TableBodyRow>
{/snippet}

{#snippet ValueWithUnitCellContent(value: string | number, unit: string, abbreviation?: string)}
	<span class={costDurationTextClasses}>
		{value}
		{#if abbreviation}
			<abbr class="text-coloredneutral-soft decoration-none" title={abbreviation}>{unit}</abbr>
		{:else}
			<span class="text-coloredneutral-soft">{unit}</span>
		{/if}
	</span>
{/snippet}

{#snippet EmptyFavoriteState()}
	<div class="flex flex-col gap-4 size-full items-center justify-center">
		<Badge
			icon="bookmark"
			class="size-22.5! rd-50% text-12"
			colorClasses="bg-neutral-soft text-coloredneutral-weak"
		/>
		<span class="font-size-4 line-height-5 text-coloredneutral-strongest font-400">{m.bookmark__no_saved_itineraries()}</span>
		<Button
			color="primary"
			variant="filled"
			text={m.itinerary__compute()}
			onclick={() => gotoRelativeUrl('/routes')}
		/>
	</div>
{/snippet}

<style>
	@media screen and (max-width: 959px) {
		:global(tbody tr) {
			grid-template-areas:
				'details'
				'numbers'
				'actions';
		}
	}
</style>
